// Transfer-Matching: Erkennt zusammengehörige Auszahlungs-/Einzahlungspaare
// (z.B. Wallet A → Wallet B) und verhindert damit falsche FIFO-Auflösung.

// 7 Tage: Blockchain-Transfers können bei Bridges deutlich länger dauern
const TRANSFER_WINDOW_MS = 7 * 24 * 3600 * 1000;

// Betrag-Toleranz 8 %: deckt Netzwerkgebühren, Bridge-Fees und Stablecoins mit
// Slippage ab. 5 % war zu eng für Fälle wie USDC-Rücktransfers (4-5 % Differenz).
const AMOUNT_TOLERANCE = 0.08;

// Zeitdecay über 3 Tage (statt 1 Tag): Same-exchange-Transfers und langsame
// Chains (z.B. Flare, XRP-Bridge) brauchen mehr Spielraum im Zeitfenster.
const TIME_DECAY_MS = 3 * 24 * 3600 * 1000;

// Reduziert auf 45: kalibiert für same-exchange-Transfers die keinen +12-Bonus
// für verschiedene Exchanges bekommen.
const MIN_SCORE = 45;

// Fiat-Währungen werden NIE automatisch als Transfer-Paar gematcht — sie sind
// Bank↔Börse-Geldflüsse, kein Wallet-zu-Wallet-Transfer. Deckt sich mit der
// Fiat-Ausblendung in Portfolio/FIFO/Display (coin∈FIAT). Manuelle Links bleiben
// erlaubt. Siehe fix_unknown_counterparty_and_fiat_flag.
const FIAT_NO_PAIR = new Set(['EUR', 'USD', 'GBP', 'CHF', 'AUD', 'CAD', 'JPY', 'TRY', 'BRL']);

/**
 * Normalisiert 0€-Verkäufe/-Käufe zu Transfers.
 * Manche Börsen-CSVs exportieren Wallet-Transfers als Kauf/Verkauf mit Preis 0.
 * Ledger-Exporte sind immer Transfers — auch wenn der AI-Parser ausnahmsweise
 * kauf/verkauf zurückgibt, korrigieren wir das hier.
 */
export function normalizeTransactions(txs) {
  return txs.map(tx => {
    // Ledger IN/OUT sind per Default Wallet-Transfers. Ausnahme: hat der User
    // den preis manuell befüllt (z.B. via CoinGecko-History in der Edit-Maske),
    // ist das ein bewusster Override — Typ und Preis bleiben erhalten.
    if (tx.exchange === 'Ledger' && !(tx.preis > 0)) {
      if (tx.typ === 'verkauf') return { ...tx, typ: 'auszahlung', preis: 0 };
      if (tx.typ === 'kauf')    return { ...tx, typ: 'einzahlung', preis: 0 };
    }
    // Allgemein: 0€-kauf/verkauf → Transfer (z.B. Binance-Wallet-Transfers)
    if (tx.typ === 'verkauf' && !tx.preis) return { ...tx, typ: 'auszahlung' };
    if (tx.typ === 'kauf'    && !tx.preis) return { ...tx, typ: 'einzahlung' };
    return tx;
  });
}

/**
 * Matcht Auszahlungen mit Einzahlungen zu Transfer-Paaren.
 *
 * Parameter:
 * - AMOUNT_TOLERANCE 8 %: deckt Gas-Fees, Bridge-Slippage und Stablecoin-Rücktransfers
 * - TIME_DECAY_MS 3 Tage: langsame Chains und same-exchange-Transfers brauchen Spielraum
 * - MIN_SCORE 45: kalibriert für same-exchange-Transfers (kein +12 Exchange-Bonus)
 * - Hash-Match hat höchste Priorität (sofortiger 100-Punkt-Match)
 *
 * @param {Array} txs Normalisierte Transaktionen
 * @returns {Array} Transfer-Paare { out, in, score, transferGroupId }
 */
/** Gibt einen präzisen Unix-Timestamp (UTC) zurück. Nutzt `tx.ts` wenn gesetzt
 *  (kanonischer Timestamp aus txFingerprint), sonst Fallback auf datum+zeit. */
function toTimestamp(tx) {
  if (tx.ts) {
    const ms = Date.parse(tx.ts);
    if (!Number.isNaN(ms)) return ms;
  }
  // Z-Suffix erzwingt UTC-Interpretation — ohne Z interpretiert JS als Lokalzeit des Users
  if (tx.zeit) return new Date(`${tx.datum}T${tx.zeit}Z`).getTime();
  return new Date(`${tx.datum}T00:00:00Z`).getTime();
}

// Bekannte 1:1-Wrapping-Paare bei denen ein Bridge-Transfer das Symbol wechselt.
// Konservative Liste: nur technisch gesicherte Pairs mit fester 1:1-Ratio.
const TRANSFER_EQUIVALENT_PAIRS = new Set([
  'ETH:WETH', 'WETH:ETH',   // EVM Wrapping (Uniswap, etc.)
  'BTC:WBTC', 'WBTC:BTC',   // Wrapped Bitcoin auf Ethereum
]);

/** Prüft ob zwei Coin-Symbole für Transfer-Matching als äquivalent gelten. */
export function symbolsAreTransferEquivalent(coinA, coinB) {
  if (!coinA || !coinB) return false;
  if (coinA === coinB) return true;
  return TRANSFER_EQUIVALENT_PAIRS.has(`${coinA}:${coinB}`);
}

export function matchTransfers(txs) {
  const normalized = normalizeTransactions(txs);
  const outs = normalized.filter(t => t.typ === 'auszahlung');
  const ins  = normalized.filter(t => t.typ === 'einzahlung');

  const pairs    = [];
  const usedIns  = new Set();
  const usedOuts = new Set();

  // ── Manuelle Links: vom Nutzer erstellt, autoritativ (vor dem Scoring) ──────
  // Ein manuelles Paar gilt nur, wenn beide TX `manualLink` haben, sich
  // gegenseitig über `linkedTxId` referenzieren und ein echtes
  // auszahlung↔einzahlung-Paar bilden. Dangling-Links werden übersprungen.
  const byId = new Map(normalized.map(t => [t._docId || String(t.id), t]));
  for (const tx of normalized) {
    if (!tx.manualLink || !tx.linkedTxId) continue;
    const id = tx._docId || String(tx.id);
    if (usedOuts.has(id) || usedIns.has(id)) continue; // 2. Begegnung des Paares
    const partner = byId.get(tx.linkedTxId);
    if (!partner || !partner.manualLink) continue;
    if (partner.linkedTxId !== id) continue;
    const out = tx.typ === 'auszahlung' ? tx : (partner.typ === 'auszahlung' ? partner : null);
    const inn = tx.typ === 'einzahlung' ? tx : (partner.typ === 'einzahlung' ? partner : null);
    if (!out || !inn || out === inn) continue;
    const outId = out._docId || String(out.id);
    const innId = inn._docId || String(inn.id);
    pairs.push({
      out, in: inn, score: 100, manual: true,
      transferGroupId: out.transferGroupId || `mtg_${outId}`,
    });
    usedOuts.add(outId);
    usedIns.add(innId);
  }

  for (const out of outs) {
    const outId   = out._docId || String(out.id);
    if (usedOuts.has(outId)) continue;
    const outTs   = toTimestamp(out);
    const outCoin = out.coin || out.sym; // coin gesetzt durch canonicalNormalizer, sym als Fallback
    // Fiat-Ein-/Auszahlungen (EUR/USD …) sind Bank↔Börse-Geldflüsse, KEIN
    // Wallet-Transfer-Paar. Niemals automatisch paaren — sonst entstünde z.B.
    // ein falsches „Coinbase → Binance" statt „Bank → Börse", und unterschiedliche
    // Beträge/Zeiträume passen ohnehin nicht exakt. Manuelle Links (oben) bleiben
    // unberührt. Bestandsneutral: Fiat ist aus dem Krypto-Portfolio ausgeschlossen.
    if (FIAT_NO_PAIR.has(String(outCoin || '').toUpperCase())) continue;
    let bestMatch = null, bestScore = MIN_SCORE - 1;

    for (const dep of ins) {
      const depId   = dep._docId || String(dep.id);
      const depCoin = dep.coin || dep.sym;
      if (usedIns.has(depId)) continue;
      if (!symbolsAreTransferEquivalent(outCoin, depCoin)) continue;

      // ── Identischer TX-Hash → sofortiger sicherer Match ──────────────────
      if (out.hash && dep.hash && out.hash === dep.hash) {
        bestMatch = dep;
        bestScore = 100;
        break;
      }

      // ── Symmetrischer On-Chain-Adress-Match → sofortiger 100-Score ──────
      // Beide Seiten via blockchainEnricher angereichert, chainFrom/chainTo
      // stimmen überein → kryptografische Gewissheit, auch wenn die hash-
      // Strings unterschiedlich sind (Bitpanda-UUID vs Stellar-Hash).
      if (
        out.chainFrom && dep.chainFrom && out.chainTo && dep.chainTo &&
        out.chainFrom === dep.chainFrom && out.chainTo === dep.chainTo
      ) {
        bestMatch = dep;
        bestScore = 100;
        break;
      }

      // ── Betrag-Prüfung ───────────────────────────────────────────────────
      const amtDiff = Math.abs(out.menge - dep.menge) / (out.menge || 1);
      if (amtDiff > AMOUNT_TOLERANCE) continue;

      // ── Zeitfenster-Prüfung ──────────────────────────────────────────────
      const depTs   = toTimestamp(dep);
      const timeDiff = Math.abs(depTs - outTs);
      // Deposit muss nach (oder gleichzeitig mit) Withdrawal sein.
      // Wenn eine Seite kein `zeit`-Feld hat, defaultet toTimestamp() auf 00:00:00 UTC.
      // Ein Ledger-Ausgang um z.B. 14:30 Uhr würde dann die Binance-Einzahlung
      // (00:00:00 = 14,5h "früher") fälschlicherweise ausschließen.
      // → Bei fehlendem Zeit-Feld 24h Spielraum statt 1h.
      // Wenn eine Seite Chain-Kontext liefert (chainTimestamp ODER chainFrom/To),
      // wissen wir, dass die CSV-Booking-Zeit der anderen Seite mehrere Stunden
      // nachhängen kann (Bitpanda bucht oft Stunden nach Stellar-Confirmation).
      // → 3 Tage Spielraum statt 1 h.
      const hasChainContext =
        !!(out.chainTimestamp || dep.chainTimestamp) ||
        !!((out.chainFrom && out.chainTo) || (dep.chainFrom && dep.chainTo));
      const backwardLimit = hasChainContext
        ? 3 * 86_400_000
        : ((out.zeit && dep.zeit) ? 3_600_000 : 86_400_000);
      if (depTs < outTs - backwardLimit) continue;
      if (timeDiff > TRANSFER_WINDOW_MS) continue;

      // ── Scoring ──────────────────────────────────────────────────────────
      // Betrag: näher = mehr Punkte (max 45)
      const amtScore = (1 - amtDiff / AMOUNT_TOLERANCE) * 45;

      // Zeit: exponentiell abnehmend über 3 Tage (< 1h ≈ 40, 3 Tage ≈ 14, 7 Tage ≈ 3)
      const timeScore = Math.exp(-timeDiff / TIME_DECAY_MS) * 40;

      // Verschiedene Exchanges = starkes Signal für echten Wallet-Transfer (max 12)
      // Gleiche Exchange = neutral (kein Bonus, kein Abzug) — same-exchange transfers
      // (z.B. Coinbase-intern, Rücktransfer) sind gültige Transfers.
      const outPlat  = (out.platform || out.exchange || '').toLowerCase();
      const depPlat  = (dep.platform || dep.exchange || '').toLowerCase();
      const platScore = (outPlat && depPlat && outPlat !== depPlat) ? 12 : 0;

      const score = amtScore + timeScore + platScore;

      if (score > bestScore) {
        bestScore = score;
        bestMatch = dep;
      }
    }

    if (bestMatch && bestScore >= MIN_SCORE) {
      const transferGroupId = `tg_${outId}`;
      pairs.push({ out, in: bestMatch, score: bestScore, transferGroupId });
      usedIns.add(bestMatch._docId || String(bestMatch.id));
    }
  }

  return pairs;
}

/**
 * Gibt eine Map txId → matchInfo zurück, verwendbar in UI-Komponenten.
 * matchInfo: { matchStatus, transferGroupId, matchedPlatform, matchedDate, matchedMenge }
 */
export function buildTransferMap(txs) {
  const pairs = matchTransfers(normalizeTransactions(txs));
  const map   = new Map();

  for (const pair of pairs) {
    const outId  = pair.out._docId  || String(pair.out.id);
    const inId   = pair.in._docId   || String(pair.in.id);
    const outPlat = pair.out.exchange || pair.out.platform || '?';
    const inPlat  = pair.in.exchange  || pair.in.platform  || '?';

    map.set(outId, {
      matchStatus:     'matched',
      transferGroupId: pair.transferGroupId,
      matchedPlatform: inPlat,
      matchedDate:     pair.in.datum,
      matchedMenge:    pair.in.menge,
      linkedTxId:      inId,
      score:           pair.score,
    });
    map.set(inId, {
      matchStatus:     'matched',
      transferGroupId: pair.transferGroupId,
      matchedPlatform: outPlat,
      matchedDate:     pair.out.datum,
      matchedMenge:    pair.out.menge,
      linkedTxId:      outId,
      score:           pair.score,
    });
  }

  // Ungematchte Transfers markieren
  // Bitget Futures: close_long/close_short sind P&L-Settlements, keine Wallet-Transfers.
  // Sie haben nie ein Gegenstück und sollen kein "Kein Gegenstück"-Warning auslösen.
  const INTERNAL_SETTLEMENT_EXCHANGES = new Set(['Bitget Futures']);

  // Fiat-Ein/Auszahlungen (EUR/USD…) sind Bank↔Börse-Geldflüsse, KEINE Krypto-
  // Wallet-Transfers. Ihr Gegenpart ist die Bank des Kunden — es gibt nichts zu
  // „verbinden". Sie dürfen daher nie als „offener Transfer" (oranger Rand)
  // erscheinen. Deckt sich mit der isNonAssetTransfer-Ausblendung im WalletScreen,
  // wo Fiat aus Zählung/Filter bereits ausgenommen ist — fixt die Inkonsistenz,
  // dass die Einzelzeile trotzdem geflaggt wurde.
  const FIAT_TRANSFER_COINS = new Set(['EUR', 'USD', 'GBP', 'CHF', 'AUD', 'CAD', 'JPY', 'TRY', 'BRL']);

  const normalized = normalizeTransactions(txs);

  const isFlaggable = (tx) =>
    (tx.typ === 'einzahlung' || tx.typ === 'auszahlung') &&
    !INTERNAL_SETTLEMENT_EXCHANGES.has(tx.exchange) &&
    !FIAT_TRANSFER_COINS.has(String(tx.coin || tx.sym || '').toUpperCase()) &&
    !tx.externalTransfer &&
    !tx.ownWalletTransfer &&  // bewiesener eigener Wallet-Transfer → kein „offener" Transfer
    !map.has(tx._docId || String(tx.id)); // schon gematcht?

  // Ungematchte Aus- UND Einzahlungen sammeln — sie sind die einzig möglichen
  // Gegenparts füreinander (Auszahlung ↔ Einzahlung).
  const unmatchedOuts = normalized.filter(t => t.typ === 'auszahlung' && isFlaggable(t));
  const unmatchedIns  = normalized.filter(t => t.typ === 'einzahlung' && isFlaggable(t));

  // Plausibles Transfer-Paar? (gleicher Coin, nahe Zeit, ähnliche Menge). Großzügiger
  // als der Auto-Matcher (14 Tage / 12 %), damit auch knappe Fehlschläge des Matchers
  // als "verbindbar" erkannt werden.
  const COUNTERPART_WINDOW_MS = 14 * 24 * 3600 * 1000;
  const COUNTERPART_AMOUNT_TOL = 0.12;
  const isPlausiblePair = (a, b) => {
    if (!symbolsAreTransferEquivalent(a.coin || a.sym, b.coin || b.sym)) return false;
    const aAmt = Number(a.menge) || 0;
    const bAmt = Number(b.menge) || 0;
    if (!(aAmt > 0) || !(bAmt > 0)) return false;
    if (Math.abs(aAmt - bAmt) / Math.max(aAmt, bAmt) > COUNTERPART_AMOUNT_TOL) return false;
    return Math.abs(toTimestamp(a) - toTimestamp(b)) <= COUNTERPART_WINDOW_MS;
  };
  // SYMMETRISCH: hat dieser Ein-/Ausgang einen plausiblen ungematchten Gegenpart der
  // jeweils ANDEREN Richtung?
  const hasPlausibleCounterpart = (tx) => {
    const pool = tx.typ === 'einzahlung' ? unmatchedOuts : unmatchedIns;
    return pool.some(other => isPlausiblePair(tx, other));
  };

  for (const tx of normalized) {
    if (!isFlaggable(tx)) continue;
    const id = tx._docId || String(tx.id);
    // Ein Ein-/Ausgang ist NUR dann ein "offener Transfer" (verbindbar, echter
    // Handlungsbedarf), wenn ein plausibler ungematchter Gegenpart der Gegenrichtung
    // existiert. OHNE Gegenpart: Einzahlung = schlichter Zufluss, Auszahlung =
    // (externer) Versand "gesendet" — beides KEIN Fehler. (User-Direktive 2026-06-24:
    // einseitige Sends — NAKMAK & Co. an externe Wallets, Withdrawals ohne erfasste
    // Gegenseite — dürfen NICHT als "Transfer ohne Gegenpart" erscheinen. Dreht die
    // alte "Auszahlung immer markieren"-Doktrin um. Rein Anzeige-/Datencheck-Ebene,
    // FIFO/Bestand unberührt; sobald beide Beine geladen sind, greift das Matching.)
    if (!hasPlausibleCounterpart(tx)) continue;
    map.set(id, { matchStatus: 'unmatched' });
  }

  return map;
}

// Fiat-Coins zählen nie als offener Krypto-Transfer (eigene Bank-Kategorie).
const OPEN_TRANSFER_FIAT = new Set(['EUR', 'USD', 'GBP', 'CHF', 'AUD', 'CAD', 'JPY', 'TRY', 'BRL']);

/**
 * Ist `tx` ein OFFENER Transfer — ein Ein-/Ausgang ohne bestätigten Gegenpart?
 * EINE Quelle der Wahrheit für die Wallet-„offene Transfers"-Zählung/-Filterung
 * UND den Datencheck (vorher dupliziert → drifteten auseinander). Bedingung:
 * typ einzahlung/auszahlung, KEIN externalTransfer, KEIN Fiat/NFT/Scam-Dust,
 * und in der transferMap als `unmatched` markiert (plausibler, aber nicht
 * bestätigter Gegenpart). Gegenpartlose Zuflüsse (Reward-Dust, Airdrops) sind
 * NICHT „unmatched" → hier bewusst NICHT enthalten (das sind Zuflüsse, keine
 * offenen Transfers).
 *
 * @param {Object} tx
 * @param {Map} transferMap            Ausgabe von buildTransferMap
 * @param {(tx:Object)=>boolean} [isDust]  Dust-Prädikat (scamDustTxIds-basiert)
 * @returns {boolean}
 */
export function isOpenTransfer(tx, transferMap, isDust) {
  const typ = tx?.typ;
  if (typ !== 'einzahlung' && typ !== 'auszahlung') return false;
  if (tx.externalTransfer) return false;
  const coin = String(tx.coin || tx.sym || '').toUpperCase();
  if (coin === 'NFT' || OPEN_TRANSFER_FIAT.has(coin)) return false;
  if (typeof isDust === 'function' && isDust(tx)) return false;
  const id = tx._docId || String(tx.id);
  return transferMap?.get(id)?.matchStatus === 'unmatched';
}

/**
 * Schreibt linkedTxId für neu gematchte Paare in Firestore (idempotent).
 * Nur Transaktionen ohne bestehendes linkedTxId werden aktualisiert.
 *
 * @param {Array}    pairs   Ausgabe von matchTransfers()
 * @param {Array}    txs     Alle geladenen Transaktionen (mit _docId)
 * @param {Function} patchTx patchTx(docId, fields) aus useTransactions
 */
export function persistTransferLinks(pairs, txs, patchTx) {
  const txMap = new Map(txs.map(t => [t._docId || String(t.id), t]));

  // `linkedTxId` ist ein RE-DERIVIERBARER Matching-Hint (matchTransfers baut die
  // Paare bei jedem Load neu) — KEIN bestands-, lot- oder haltefrist-prägendes
  // Feld. Schlägt ein Patch fehl, weil das Ziel-Doc zwischen onSnapshot und
  // updateDoc verschwand (Reimport-Replace-Race, MoonPay-Ledger-Scrub, oder ein
  // an einem anderen Origin gelöschtes Doc, das der persistentLocalCache noch
  // liefert — siehe Origin-Divergenz-Doktrin), ist der Write ein No-Op: der
  // In-Memory-Matcher findet das Paar beim nächsten Render erneut. Da der Aufruf
  // aus dem Auto-Matcher-Effekt fire-and-forget kommt, MUSS jede Rejection
  // geschluckt werden — sonst eskaliert sie zur unhandled rejection (CRA-Crash-
  // Overlay „No document to update"). Andere Fehler bleiben sichtbar (warn).
  const safePatch = (id, fields) => Promise.resolve()
    .then(() => patchTx(id, fields))
    .catch((err) => {
      if (!/No document to update/i.test(err?.message || '')) {
        console.warn('[persistTransferLinks] Link-Patch fehlgeschlagen:', err);
      }
    });

  const writes = [];
  for (const { out, in: inTx } of pairs) {
    const outId = out._docId || String(out.id);
    const inId  = inTx._docId || String(inTx.id);
    const outDoc = txMap.get(outId);
    const inDoc  = txMap.get(inId);
    if (outDoc?._docId && !outDoc.linkedTxId) writes.push(safePatch(outDoc._docId, { linkedTxId: inId }));
    if (inDoc?._docId  && !inDoc.linkedTxId)  writes.push(safePatch(inDoc._docId,  { linkedTxId: outId }));
  }
  return Promise.all(writes);
}

/**
 * Bewertet ein Transfer-Paar-Kandidaten für die manuelle Verbindung.
 * Reine Funktion — genutzt von Vorschlags-Ranking und Vorschau-Validierung.
 *
 * @param {Object} a Erste Transaktion (einzahlung oder auszahlung)
 * @param {Object} b Zweite Transaktion
 * @returns {Object} { valid, out, in, sameCurrency, amountDiffPct, timeDiffMs,
 *                      networkFee, score, quality, blockers[], warnings[] }
 */
export function evaluateTransferPair(a, b) {
  const blockers = [];
  const warnings = [];

  // ── Richtung: ein Transfer braucht genau 1 Ausgang + 1 Eingang ─────────────
  let out = null, inn = null;
  if (a?.typ === 'auszahlung' && b?.typ === 'einzahlung') { out = a; inn = b; }
  else if (a?.typ === 'einzahlung' && b?.typ === 'auszahlung') { out = b; inn = a; }

  if (!out || !inn) {
    blockers.push({
      code: 'direction',
      message: 'Ein Transfer braucht einen Ausgang und einen Eingang.',
    });
    return {
      valid: false, out: null, in: null, sameCurrency: false,
      amountDiffPct: 0, timeDiffMs: 0, networkFee: 0,
      score: 0, quality: 'weak', blockers, warnings,
    };
  }

  // ── Bereits verbunden ──────────────────────────────────────────────────────
  if (out.linkedTxId || inn.linkedTxId) {
    blockers.push({
      code: 'linked',
      message: 'Eine der Transaktionen ist bereits Teil eines Transfers.',
    });
  }

  // ── Währung: harte Sperre (Ausnahme: bekannte 1:1-Paare) ───────────────────
  const outCoin = out.coin || out.sym;
  const inCoin  = inn.coin || inn.sym;
  const sameCurrency = symbolsAreTransferEquivalent(outCoin, inCoin);
  if (!sameCurrency) {
    blockers.push({
      code: 'currency',
      message: 'Unterschiedliche Währungen können nicht verbunden werden.',
    });
  }

  // ── Betrag ─────────────────────────────────────────────────────────────────
  const amountDiffPct = out.menge ? Math.abs(out.menge - inn.menge) / out.menge : 0;
  const networkFee    = out.menge - inn.menge; // > 0 → Differenz als Netzwerkgebühr
  if (amountDiffPct > AMOUNT_TOLERANCE) {
    warnings.push({
      code: 'amount',
      message: `Die Beträge weichen stark ab (${(amountDiffPct * 100).toFixed(1)} %). Bitte prüfen.`,
    });
  }

  // ── Zeit ───────────────────────────────────────────────────────────────────
  const outTs = toTimestamp(out);
  const inTs  = toTimestamp(inn);
  const timeDiffMs = Math.abs(inTs - outTs);
  if (inTs < outTs - 86_400_000) {
    warnings.push({
      code: 'order',
      message: 'Die Einzahlung liegt vor der Auszahlung. Bitte prüfen.',
    });
  }
  if (timeDiffMs > TRANSFER_WINDOW_MS) {
    warnings.push({
      code: 'time',
      message: `Zwischen den Buchungen liegen ${Math.round(timeDiffMs / 86_400_000)} Tage.`,
    });
  }

  // ── Weicher Score (gleiche Logik wie matchTransfers) ───────────────────────
  const amtScore  = Math.max(0, 1 - amountDiffPct / AMOUNT_TOLERANCE) * 45;
  const timeScore = Math.exp(-timeDiffMs / TIME_DECAY_MS) * 40;
  const outPlat   = (out.platform || out.exchange || '').toLowerCase();
  const inPlat    = (inn.platform || inn.exchange || '').toLowerCase();
  const platScore = (outPlat && inPlat && outPlat !== inPlat) ? 12 : 0;
  const score     = Math.max(0, Math.min(100, Math.round(amtScore + timeScore + platScore)));
  const quality   = score >= 75 ? 'strong' : score >= 50 ? 'medium' : 'weak';

  return {
    valid: true, out, in: inn, sameCurrency,
    amountDiffPct, timeDiffMs, networkFee,
    score, quality, blockers, warnings,
  };
}
